if (!customElements.get('carlync-homepage')) {
  class CarLyncHomepage extends HTMLElement {
    connectedCallback() {
      this.modeTabs = Array.from(this.querySelectorAll('[data-cl-mode]'));
      this.modePanels = Array.from(this.querySelectorAll('[data-cl-panel]'));
      this.modeCards = Array.from(this.querySelectorAll('[data-cl-mode-card]'));
      this.stickyBar = this.querySelector('[data-cl-sticky]');
      this.hero = this.querySelector('.cl-home-hero');
      this.heroSlides = Array.from(this.querySelectorAll('[data-cl-hero-slide]'));
      this.heroControls = this.querySelector('[data-cl-hero-controls]');
      this.heroCounter = this.querySelector('[data-cl-hero-counter]');
      this.heroIndex = 0;
      this.onHeroClick = this.onHeroClick.bind(this);
      this.onHeroKeydown = this.onHeroKeydown.bind(this);
      this.onHeroVisibilityChange = this.restartHeroAutoplay.bind(this);
      if (this.heroControls && this.heroSlides.length > 1) {
        this.showHeroSlide(0);
        this.heroControls.hidden = false;
        this.heroControls.addEventListener('click', this.onHeroClick);
        this.heroControls.addEventListener('keydown', this.onHeroKeydown);
        document.addEventListener('visibilitychange', this.onHeroVisibilityChange);
        this.restartHeroAutoplay();
      }

      this.onTabClick = this.onTabClick.bind(this);
      this.onTabKeydown = this.onTabKeydown.bind(this);

      this.modeTabs.forEach((tab) => {
        tab.addEventListener('click', this.onTabClick);
        tab.addEventListener('keydown', this.onTabKeydown);
      });

      if (this.hero && this.stickyBar && 'IntersectionObserver' in window) {
        this.stickyObserver = new IntersectionObserver(
          ([entry]) => {
            const passedHero = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
            this.stickyBar.classList.toggle('is-visible', passedHero);
            this.stickyBar.setAttribute('aria-hidden', String(!passedHero));
            this.stickyBar.inert = !passedHero;
          },
          { threshold: 0 }
        );
        this.stickyObserver.observe(this.hero);
      }
    }

    disconnectedCallback() {
      this.modeTabs?.forEach((tab) => {
        tab.removeEventListener('click', this.onTabClick);
        tab.removeEventListener('keydown', this.onTabKeydown);
      });
      this.stickyObserver?.disconnect();
      this.heroControls?.removeEventListener('click', this.onHeroClick);
      this.heroControls?.removeEventListener('keydown', this.onHeroKeydown);
      document.removeEventListener('visibilitychange', this.onHeroVisibilityChange);
      window.clearTimeout(this.heroTimer);
      this.heroAnimations?.forEach((animation) => animation.cancel());
    }

    onHeroClick(event) {
      const button = event.target.closest('[data-cl-hero-direction]');
      if (!button) return;
      this.showHeroSlide(this.heroIndex + Number(button.dataset.clHeroDirection));
      this.restartHeroAutoplay();
    }

    onHeroKeydown(event) {
      let nextIndex;
      if (event.key === 'ArrowRight') nextIndex = this.heroIndex + 1;
      if (event.key === 'ArrowLeft') nextIndex = this.heroIndex - 1;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.heroSlides.length - 1;
      if (nextIndex === undefined) return;
      event.preventDefault();
      this.showHeroSlide(nextIndex);
      this.restartHeroAutoplay();
    }

    restartHeroAutoplay() {
      window.clearTimeout(this.heroTimer);
      if (!this.isConnected || document.hidden || this.heroSlides.length < 2) return;
      this.heroTimer = window.setTimeout(() => {
        this.showHeroSlide(this.heroIndex + 1);
        this.restartHeroAutoplay();
      }, 5000);
    }

    showHeroSlide(index) {
      const previousIndex = this.heroIndex;
      const direction = index < previousIndex ? -1 : 1;
      this.heroAnimations?.forEach((animation) => animation.cancel());
      this.heroAnimations = [];
      this.heroIndex = (index + this.heroSlides.length) % this.heroSlides.length;
      this.heroSlides.forEach((slide, slideIndex) => {
        slide.hidden = slideIndex !== this.heroIndex;
        slide.removeAttribute('aria-hidden');
      });
      if (this.heroCounter) this.heroCounter.textContent = `Image ${this.heroIndex + 1} of ${this.heroSlides.length}`;
      if (previousIndex === this.heroIndex || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const outgoing = this.heroSlides[previousIndex];
      const incoming = this.heroSlides[this.heroIndex];
      outgoing.hidden = false;
      outgoing.setAttribute('aria-hidden', 'true');
      incoming.removeAttribute('aria-hidden');
      const options = { duration: 550, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)' };
      const outgoingAnimation = outgoing.animate([
        { transform: 'translateX(0)' },
        { transform: `translateX(${-direction * 100}%)` },
      ], options);
      const incomingAnimation = incoming.animate([
        { transform: `translateX(${direction * 100}%)` },
        { transform: 'translateX(0)' },
      ], options);
      this.heroAnimations = [outgoingAnimation, incomingAnimation];
      incomingAnimation.onfinish = () => {
        outgoing.hidden = true;
        this.heroAnimations = [];
      };
    }

    onTabClick(event) {
      this.activateMode(event.currentTarget.dataset.clMode, true);
    }

    onTabKeydown(event) {
      const currentIndex = this.modeTabs.indexOf(event.currentTarget);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.modeTabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.modeTabs.length) % this.modeTabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.modeTabs.length - 1;

      if (nextIndex === currentIndex) return;

      event.preventDefault();
      const nextTab = this.modeTabs[nextIndex];
      this.activateMode(nextTab.dataset.clMode, true);
      nextTab.focus();
    }

    activateMode(mode, moveFocus = false) {
      this.modeTabs.forEach((tab) => {
        const isActive = tab.dataset.clMode === mode;
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
        if (isActive && moveFocus) tab.focus({ preventScroll: true });
      });

      this.modePanels.forEach((panel) => {
        panel.hidden = panel.dataset.clPanel !== mode;
      });

      this.modeCards.forEach((card) => {
        card.hidden = card.dataset.clModeCard !== mode;
      });
    }
  }

  customElements.define('carlync-homepage', CarLyncHomepage);
}
